import React, {useCallback, useMemo} from 'react';
import {
  useColorThemeType,
  useFontTheme,
} from '../../../../appStyles/hooks/theme';

import AddSignature from '../../../../../src/components/core/icon/AddSignature';
import {Analytics} from '@buncha/config/analytics/analytics';
import CheckIcon from '../../../../../src/components/core/icon/CheckIcon';
import {CustomText} from '../../../../components/core/text';
import {DriverScreens} from '../../../../../src/driver/navigation/type';
import {EventType} from '@buncha/config/analytics/type';
import {HBox} from '@buncha/components/core/view';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '../../../../../src/components/conditional/If';
import {MainScreens} from '@buncha/navigation/types';
import {Modules} from '@buncha/utils/types';
import {StyleSheet} from 'react-native';
import {Touchable} from '../../../../components/core/touchable';
import {WarningIcon} from 'native-base';
import {navigation} from '../../../../../src/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';

interface AddSignatureCellProps {
  orderId: string;
  signatureUrl: string;
  runId: number;
  isRequired?: boolean;
  taskId: string;
}

const AddSignatureCell: React.FC<AddSignatureCellProps> = props => {
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const orderId = Number(props?.orderId) || 0;
  const runId = Number(props?.runId) || 0;
  const taskId = props?.taskId;
  const signatureUrl = Boolean(props?.signatureUrl) || false;
  const {body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const getCellText = useMemo(() => {
    if (signatureUrl) return body?.signAdded;
    return body?.addSign;
  }, [body?.addSign, body?.signAdded, signatureUrl]);

  const navigateToSignature = useCallback(() => {
    Analytics.eventWithProps('Navigate to Signature screen', {
      type: EventType.cta,
      orderId: orderId,
      userType: 'driver',
    });
    navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.Signature,
      params: {orderId: orderId, runId: runId, taskId},
    });
  }, [orderId, runId, taskId]);

  return (
    <Touchable
      style={[
        styles.parentBox,
        {
          borderColor: theme?.text.gray[100],
          backgroundColor: signatureUrl
            ? theme?.background.blue[100]
            : theme?.background.white[100],
        },
      ]}
      disabled={signatureUrl}
      onPress={navigateToSignature}>
      <If condition={props.isRequired && !signatureUrl}>
        <WarningIcon style={styles.warningIcon} color={theme?.icon.red[100]} />
      </If>
      <If condition={!signatureUrl}>
        <AddSignature
          height={24}
          width={24}
          fill={theme?.icon?.blue?.[300]}
          style={styles.icon}
          viewBox="-1 2 39 30"
        />
      </If>
      <If condition={signatureUrl}>
        <CheckIcon
          height={24}
          width={24}
          fill={theme?.icon?.white?.[100]}
          style={styles.icon}
          viewBox="-2 3 44 30"
        />
      </If>
      <HBox style={styles.addedContainer}>
        <CustomText
          style={[
            styles.text,
            {
              color: signatureUrl
                ? theme?.text?.white?.[100]
                : theme?.text?.blue?.[300],
              fontFamily: muli?.[700]?.normal,
            },
          ]}>
          {getCellText}
        </CustomText>
      </HBox>
    </Touchable>
  );
};

export default React.memo(AddSignatureCell);

const styles = StyleSheet.create({
  parentBox: {
    justifyContent: 'center',
    paddingHorizontal: 2,
    paddingVertical: 15,
    borderRadius: 8,
    flex: 1,
    borderWidth: 0.5,
  },
  text: {
    marginTop: 5,
  },
  icon: {
    alignSelf: 'center',
  },
  warningIcon: {position: 'absolute', right: 2, top: 2},
  addedContainer: {justifyContent: 'center', alignItems: 'center', gap: 2},
});
